/**
 * Phase 3a §3 — Shared portal chrome.
 *
 * Reusable for both employer (Phase 3a) and employee (Phase 3b) portals.
 * Wraps the page with a header + footer that match the marketing site
 * design tokens. Customer-facing support copy always says "Success Team"
 * — never a person's name.
 *
 * Props:
 *   actorType        "employer" | "employee"
 *   displayName      string    (shown as a chip inside the drawer)
 *   signOutHref      string | null  (null hides the sign-out link)
 *   onSignOut        function  (override for custom confirm flows)
 *   headerAccessory  ReactNode (breadcrumb + session timer slot)
 *   children         main content
 *
 * Navigation model (course-correction — matches marketing nav exactly,
 * only bg colour + link set differ):
 *   Desktop (> 900px): header spans the full viewport; inline cluster
 *       [Laguna]  [accessory]   [SUCCESS TEAM] [whoami] [SIGN OUT] [EN/ES]
 *       Logo sits at the left viewport edge, cluster at the right edge,
 *       using the same 2rem padding as `.lg-nav`.
 *   Mobile  (≤ 900px): collapses to [Laguna] [accessory] [EN/ES] [☰]
 *       with the drawer hosting the nav items. Uses the same 900px
 *       breakpoint + `.desktop-only` / `.mobile-only` utility classes
 *       as the marketing nav.
 *   Privacy / Terms live in the footer only — never in the drawer.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import LanguageSwitcher from "../LanguageSwitcher";
import { CALENDLY_SUCCESS_TEAM_URL } from "../../config/constants";

import "./PortalChrome.css";

// POST-only endpoints (Method Not Allowed on GET). Anchor navigation
// here would expose raw {"detail":"Method Not Allowed"} to the user;
// handleDefaultSignOut below fetches them correctly and then
// redirects to the public root.
const DEFAULT_SIGN_OUT_BY_ACTOR = {
    employer: "/api/employer/logout",
    employee: "/api/employee/logout",       // Phase 3b — exists as a shim only today
};

const POST_REDIRECT_AFTER_SIGN_OUT = "/";

const SUCCESS_TEAM_URL = CALENDLY_SUCCESS_TEAM_URL;

export default function PortalChrome({
    actorType = "employer",
    displayName,
    signOutHref,
    onSignOut,
    headerAccessory = null,
    children,
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const burgerRef = useRef(null);
    const drawerFirstFocusableRef = useRef(null);

    // `signOutHref === null` explicitly means "don't render sign-out"
    // (used by the anonymous employee wizard in Phase 3 — no session to
    // clear). `undefined` falls back to the actor default.
    const omitSignOut = signOutHref === null;
    const resolvedSignOut =
        signOutHref || DEFAULT_SIGN_OUT_BY_ACTOR[actorType] || "/";

    // Default sign-out: fetch-POST the logout endpoint (all three
    // backend logout routes are POST-only). Custom `onSignOut` still
    // wins for PortalProposal's confirm-dialog flow.
    const handleDefaultSignOut = async () => {
        const endpoint = DEFAULT_SIGN_OUT_BY_ACTOR[actorType];
        if (endpoint) {
            try {
                await fetch(endpoint, { method: "POST", credentials: "include" });
            } catch (_) { /* network blip — still sign out locally */ }
        }
        navigate(POST_REDIRECT_AFTER_SIGN_OUT, { replace: true });
    };

    const handleSignOutClick = (e) => {
        e.preventDefault();
        setDrawerOpen(false);
        if (onSignOut) {
            onSignOut();
            return;
        }
        handleDefaultSignOut();
    };

    // ---- Drawer wiring ----------------------------------------------------
    // Scroll-lock + ESC + focus management. Mirrors Nav.jsx's pattern so
    // mobile users get consistent behaviour between the marketing nav and
    // the portal nav.
    useEffect(() => {
        if (!drawerOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [drawerOpen]);

    useEffect(() => {
        if (!drawerOpen) return;
        const onKey = (e) => {
            if (e.key === "Escape") setDrawerOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [drawerOpen]);

    // Focus the first drawer item on open; restore to the burger on close.
    useEffect(() => {
        if (drawerOpen) {
            const timer = setTimeout(() => {
                if (drawerFirstFocusableRef.current) {
                    drawerFirstFocusableRef.current.focus();
                }
            }, 0);
            return () => clearTimeout(timer);
        }
        // When drawer closes, return focus to the burger — only if focus
        // is still on a drawer item (avoid stealing focus mid-typing).
        if (
            burgerRef.current &&
            document.activeElement &&
            document.activeElement.closest("[data-testid='portal-chrome-drawer']")
        ) {
            burgerRef.current.focus();
        }
    }, [drawerOpen]);

    return (
        <div className="portal-chrome" data-testid="portal-chrome" data-actor={actorType}>
            <header className="portal-chrome-header" role="banner">
                <div className="portal-chrome-header-inner">
                    <Link
                        to="/"
                        className="portal-chrome-wordmark"
                        data-testid="portal-chrome-wordmark"
                    >
                        <span className="laguna-mark">Laguna</span>
                    </Link>

                    {headerAccessory && (
                        <div
                            className="portal-chrome-accessory"
                            data-testid="portal-chrome-accessory"
                        >
                            {headerAccessory}
                        </div>
                    )}

                    {/* Desktop inline cluster — mirrors marketing nav's .nav-right
                        pattern, just with link set + paper colour for the dark bg.
                        Hidden at ≤900px via the shared .desktop-only utility. */}
                    <div className="portal-chrome-desktop desktop-only" data-testid="portal-chrome-desktop">
                        <a
                            href={SUCCESS_TEAM_URL}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="portal-chrome-desktop-link"
                            data-testid="portal-chrome-desktop-success-team"
                        >
                            {t("portal.shared.successTeamChip")}
                        </a>
                        {displayName && (
                            <div
                                className="portal-chrome-desktop-whoami mono"
                                data-testid="portal-chrome-desktop-whoami"
                                title={displayName}
                            >
                                <span className="portal-chrome-desktop-whoami-label">
                                    {t("portal.shared.signedInAs")}
                                </span>
                                <span className="portal-chrome-desktop-whoami-name">
                                    {displayName}
                                </span>
                            </div>
                        )}
                        {!omitSignOut && (
                            <>
                                {actorType === "employer" && (
                                    <Link
                                        to="/portal/employer/quotes"
                                        className="portal-chrome-desktop-link"
                                        data-testid="portal-chrome-desktop-portal"
                                    >
                                        {t("portal.shared.portalLink", { defaultValue: "Portal" })}
                                    </Link>
                                )}
                                <a
                                    href={resolvedSignOut}
                                    onClick={handleSignOutClick}
                                    className="portal-chrome-desktop-link portal-chrome-desktop-signout"
                                    data-testid="portal-chrome-desktop-signout"
                                >
                                    {t("portal.shared.signOut")}
                                </a>
                            </>
                        )}
                        <LanguageSwitcher compact />
                    </div>

                    {/* Mobile right cluster — EN/ES + hamburger. Shown at ≤900px. */}
                    <div className="portal-chrome-right mobile-only" data-testid="portal-chrome-mobile-right">
                        <LanguageSwitcher compact />
                        <button
                            ref={burgerRef}
                            type="button"
                            className="portal-chrome-burger"
                            data-testid="portal-chrome-burger"
                            aria-label={drawerOpen ? t("portal.shared.drawerClose") : t("portal.shared.drawerOpen")}
                            aria-expanded={drawerOpen}
                            aria-controls="portal-chrome-drawer"
                            onClick={() => setDrawerOpen((v) => !v)}
                        >
                            <span className={`portal-burger-bar ${drawerOpen ? "is-open" : ""}`} />
                            <span className={`portal-burger-bar ${drawerOpen ? "is-open" : ""}`} />
                            <span className={`portal-burger-bar ${drawerOpen ? "is-open" : ""}`} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Slide-over drawer — hosts the folded portal nav items.
                Scrim click-out + ESC close handled above. */}
            <div
                className={`portal-chrome-drawer-scrim ${drawerOpen ? "is-open" : ""}`}
                data-testid="portal-chrome-drawer-scrim"
                onClick={() => setDrawerOpen(false)}
                aria-hidden={!drawerOpen}
            />
            <aside
                id="portal-chrome-drawer"
                className={`portal-chrome-drawer ${drawerOpen ? "is-open" : ""}`}
                data-testid="portal-chrome-drawer"
                aria-hidden={!drawerOpen}
                role="dialog"
                aria-modal="true"
                aria-label={t("portal.shared.drawerAria")}
            >
                <div className="portal-chrome-drawer-inner">
                    <a
                        ref={drawerFirstFocusableRef}
                        href={SUCCESS_TEAM_URL}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="portal-chrome-drawer-link portal-chrome-drawer-primary"
                        data-testid="portal-chrome-drawer-success-team"
                        onClick={() => setDrawerOpen(false)}
                    >
                        {t("portal.shared.successTeamChip")}
                    </a>
                    {displayName && (
                        <div
                            className="portal-chrome-drawer-whoami mono"
                            data-testid="portal-chrome-drawer-whoami"
                            title={displayName}
                        >
                            <span className="portal-chrome-drawer-whoami-label">
                                {t("portal.shared.signedInAs")}
                            </span>
                            <span className="portal-chrome-drawer-whoami-name">
                                {displayName}
                            </span>
                        </div>
                    )}
                    {!omitSignOut && (
                        <>
                            {actorType === "employer" && (
                                <Link
                                    to="/portal/employer/quotes"
                                    className="portal-chrome-drawer-link portal-chrome-drawer-portal mono"
                                    data-testid="portal-chrome-drawer-portal"
                                    onClick={() => setDrawerOpen(false)}
                                >
                                    {t("portal.shared.portalLink", { defaultValue: "Portal" })}
                                </Link>
                            )}
                            <hr className="portal-chrome-drawer-divider" aria-hidden="true" />
                            <a
                                href={resolvedSignOut}
                                onClick={handleSignOutClick}
                                className="portal-chrome-drawer-link portal-chrome-drawer-signout mono"
                                data-testid="portal-chrome-drawer-signout"
                            >
                                {t("portal.shared.signOut")}
                            </a>
                        </>
                    )}
                    {/* Privacy / Terms intentionally omitted — footer only. */}
                </div>
            </aside>

            <main className="portal-chrome-main" role="main">
                {children}
            </main>

            <footer className="portal-chrome-footer" role="contentinfo">
                <div className="portal-chrome-footer-inner mono">
                    <span>© {new Date().getFullYear()} Laguna Benefits</span>
                    <span aria-hidden="true">·</span>
                    <Link to="/privacy">{t("portal.shared.privacy")}</Link>
                    <span aria-hidden="true">·</span>
                    <Link to="/terms">{t("portal.shared.terms")}</Link>
                    <span aria-hidden="true">·</span>
                    <span className="portal-chrome-footer-legal">
                        {t("portal.shared.canSpamLine")}
                    </span>
                </div>
            </footer>
        </div>
    );
}
